import config from '@/config/config.ts';

type TBootError = {
  title: string,
  message: string,
  detail?: string,
};

/**
 * The boot screen is rendered by `index.html` before the app is mounted and runs before i18n is set up,
 * so its texts are static English.
 */
export function showBootError(bootError: TBootError): void {
  const bootScreen = document.querySelector<HTMLElement>('#app .boot-screen');
  if (bootScreen === null) {
    return;
  }

  const title = document.createElement('h1');
  title.className = 'boot-screen__title';
  title.textContent = bootError.title;

  const message = document.createElement('p');
  message.className = 'boot-screen__message';
  message.textContent = bootError.message;

  bootScreen.classList.add('boot-screen--error');
  bootScreen.setAttribute('role', 'alert');
  bootScreen.querySelector('.boot-screen__spinner')?.remove();
  bootScreen.querySelector('.boot-screen__message')?.remove();
  bootScreen.append(title, message);

  if (bootError.detail) {
    const detail = document.createElement('p');
    detail.className = 'boot-screen__detail';
    detail.textContent = bootError.detail;
    bootScreen.append(detail);
  }
}

/**
 * Keycloak signs in with PKCE, which needs the Web Crypto API. Browsers only provide it in a secure
 * context, i.e. on HTTPS or localhost, so on plain HTTP signing in fails.
 */
export function isWebCryptoAvailable(): boolean {
  return window.isSecureContext && typeof window.crypto?.subtle !== 'undefined';
}

export function showInsecureContextError(): void {
  showBootError({
    title: 'Qmulus requires a secure connection',
    message: 'Qmulus was opened over an insecure connection (HTTP). Browsers only provide the security '
      + 'features required for signing in on secure connections, so Qmulus cannot start. Open Qmulus via '
      + 'an address starting with https:// or contact your administrator.',
    detail: `Current address: ${window.location.href}`,
  });
}

/**
 * Keycloak rejects with `undefined` or with plain objects such as `{ error, error_description }`
 * instead of `Error` instances.
 */
function describeStartupError(error: unknown): string | undefined {
  if (error === undefined || error === null) {
    return undefined;
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'object') {
    return JSON.stringify(error);
  }
  return String(error);
}

export function showStartupError(error: unknown): void {
  const errorDescription = describeStartupError(error);
  const contactSupport = errorDescription
    ? `contact ${config.VITE_APP_SUPPORT_EMAIL} and include the error message shown below, so we can help you faster.`
    : `contact ${config.VITE_APP_SUPPORT_EMAIL}.`;
  showBootError({
    title: 'Qmulus could not be started',
    message: `Please reload the page. If the problem persists, ${contactSupport}`,
    detail: errorDescription ? `Error: ${errorDescription}` : undefined,
  });
}
